// import hooks
import { useMemo } from 'react';
import useGetEmployeeWorkAgreements from 'components/employee-portal/leave-requests/hooks/useGetEmployeeWorkAgreements';
import useGetDutyHourTypes from '../hooks/useGetDutyHourTypes';

// import queries & mutations
import { queries } from 'RestAPIs/rapini';

// import types
import { Option } from 'components/shared/DutyCodeSelector.types';
import { UseDutyLineOptionsProps } from './useDutyLineOptions.types';

// import utils
import dayjs from 'dayjs';

export const useDutyLineOptions = ({
  fetchKind,
  startDate,
  endDate,
  employeeId,
  date // organizationalUnitId
}: UseDutyLineOptionsProps) => {
  // work agreements
  const { data: workAgreements = [], isFetching: waFetching } =
    fetchKind === 'workAgreement' && employeeId
      ? useGetEmployeeWorkAgreements(
          {
            startDate: dayjs(startDate).toDate(),
            endDate: dayjs(endDate).toDate(),
            employeeId
          },
          {
            enabled: !!startDate && !!endDate && !!employeeId,
            staleTime: 30 * 60 * 1000,
            cacheTime: 2 * 60 * 60 * 1000
          }
        )
      : { data: [], isFetching: false };

  // duty codes
  const { data: dutyCodes = [], isFetching: dcFetching } =
    fetchKind === 'basicDuty'
      ? queries.useGetDutyCodes(
          dayjs(date, 'YYYY-MM-DD', true).toISOString(),
          undefined,
          false,
          {
            enabled: !!date,
            staleTime: 30 * 60 * 1000,
            cacheTime: 2 * 60 * 60 * 1000
          }
        )
      : { data: [], isFetching: false };

  // duty-hour types
  const { data: dutyHourTypes = [], isFetching: dhtFetching } =
    fetchKind === 'dutyHourTypes' && startDate && endDate
      ? useGetDutyHourTypes(
          { startDate, endDate },
          { enabled: !!startDate && !!endDate }
        )
      : { data: [], isFetching: false };

  // build <Autocomplete>-ready options
  const options: Option[] = useMemo(() => {
    switch (fetchKind) {
      case 'workAgreement':
        return workAgreements.map((workAgreement) => ({
          value: workAgreement.waId,
          label:
            `${workAgreement.nr} - ${workAgreement.functionDescription} - ` +
            `${workAgreement.brNameShort} - ${workAgreement.contractKind}`,
          id: workAgreement.nr,
          key: workAgreement.waId ?? crypto.randomUUID()
        }));
      case 'basicDuty':
        return dutyCodes.map((duty) => ({
          value: duty.dutyCodeId,
          label: duty.dutyCode?.startsWith('§')
            ? duty.code!
            : // : `${duty.code} (${duty.dutyCode})`,
              `${duty.dutyCode}`,
          id: duty.dutyCodeId,
          key: duty.dutyCodeId ?? crypto.randomUUID()
        }));
      case 'dutyHourTypes':
        return dutyHourTypes
          .filter((type) => type.dccFlags.includes('ALLOWED_WITH_NOMINAL'))
          .map((type) => ({
            value: type.id,
            label: `${type.code} - ${type.description}`,
            id: type.code,
            key: type.id ?? crypto.randomUUID()
          }));
      default:
        return [];
    }
  }, [fetchKind, workAgreements, dutyCodes, dutyHourTypes]);

  return {
    options,
    dutyCodes,
    isFetching: waFetching || dcFetching || dhtFetching
  };
};
